import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'

const springConfig = { type: "spring", stiffness: 60, damping: 10 }

const navItems = [
  { name: 'PC', href: '/', isRoute: true, icon: 'PC' },
  { name: 'About', href: '#about', isRoute: false, icon: '👤' },
  { name: 'Projects', href: '#projects', isRoute: false, icon: '💼' },
  { name: 'Resume', href: '#resume', isRoute: false, icon: '📄' },
  { name: 'Contact', href: '#contact', isRoute: false, icon: '📧' },
]

export default function Navigation() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [activeSection, setActiveSection] = useState('')

  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname === '/') {
        const sections = navItems
          .filter(item => !item.isRoute)
          .map(item => item.href.substring(1))
        
        let currentSection = ''
        const scrollPosition = window.scrollY + 150
        
        for (const sectionId of sections) {
          const element = document.getElementById(sectionId)
          if (element) {
            const rect = element.getBoundingClientRect()
            const elementTop = rect.top + window.scrollY
            const elementBottom = elementTop + rect.height
            
            if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
              currentSection = `#${sectionId}`
              break
            }
          }
        }
        
        // Only set active section if we're actually in a section
        // Don't highlight Home when at top - only highlight when in a specific section
        setActiveSection(currentSection)
      }
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [location.pathname])

  const handleNavClick = (href: string, isRoute: boolean) => {
    if (isRoute) {
      if (location.pathname !== href) {
        navigate(href)
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    } else {
      if (location.pathname === '/') {
        const element = document.querySelector(href)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      } else {
        navigate('/')
        setTimeout(() => {
          const element = document.querySelector(href)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }, 100)
      }
    }
  }

  return (
    <>
      <motion.nav
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
        className="fixed bottom-4 sm:bottom-8 left-2 right-2 sm:left-0 sm:right-0 z-50 flex justify-center items-center"
      >
        <div className="glass px-3 py-2 sm:px-5 sm:py-2.5 flex items-center gap-1 sm:gap-2 rounded-2xl max-w-full overflow-x-auto">
          {/* Navigation Items */}
          <div className="flex items-center gap-1 sm:gap-2">
            {navItems.map((item) => (
              <motion.a
                key={item.name}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault()
                  handleNavClick(item.href, item.isRoute)
                }}
                data-magnetic
                className={`glass-neu relative px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all min-w-[44px] sm:min-w-0 flex items-center justify-center ${
                  item.name === 'PC' ? 'font-semibold font-display' : ''
                } ${
                  (!item.isRoute && activeSection === item.href) || (item.isRoute && location.pathname === item.href && item.href !== '/') || (item.href === '/' && location.pathname === '/' && activeSection === '')
                    ? item.name === 'PC' ? 'text-gradient' : 'text-accent'
                    : 'text-text-primary/70 hover:text-accent'
                }`}
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                transition={springConfig}
              >
                {item.name === 'PC' ? (
                  <span className="text-xs sm:text-sm font-semibold font-display">PC</span>
                ) : (
                  <>
                    <span className="text-lg sm:hidden">{item.icon}</span>
                    <span className="hidden sm:inline">{item.name}</span>
                  </>
                )}
                {((!item.isRoute && activeSection === item.href) || (item.isRoute && location.pathname === item.href && item.href !== '/') || (item.href === '/' && location.pathname === '/' && activeSection === '')) && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-accent"
                    initial={false}
                    transition={springConfig}
                  />
                )}
              </motion.a>
            ))}
          </div>
          
          {/* Divider */}
          <div className="w-px h-5 sm:h-6 bg-shadow-dark/20 mx-1 sm:mx-2 hidden sm:block" />
          
          {/* Dashboard Login Button */}
          {user ? (
            <motion.a
              href="/dashboard"
              onClick={(e) => {
                e.preventDefault()
                navigate('/dashboard')
              }}
              data-magnetic
              className="glass-neu relative px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all text-accent min-w-[44px] sm:min-w-0"
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
            >
              <span className="hidden sm:inline">Dashboard</span>
              <span className="sm:hidden">⚙️</span>
            </motion.a>
          ) : (
            <motion.a
              href="/dashboard/login"
              onClick={(e) => {
                e.preventDefault()
                navigate('/dashboard/login')
              }}
              data-magnetic
              className="glass-neu relative px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all text-text-primary/70 hover:text-accent min-w-[44px] sm:min-w-0"
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
            >
              <span className="hidden sm:inline">Login</span>
              <span className="sm:hidden">🔐</span>
            </motion.a>
          )}
        </div>
      </motion.nav>

    </>
  )
}
